import { useParams } from "react-router-dom";
import NavBar from "../NavBar";
import "./PublicationsDetails.scss";
import LoadingOverlay from "components/blocks/LoadingOverlay";
import { useTranslation } from "react-i18next";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import { useCookieContext } from "contexts";
import PublicationBookCard from "./PublicationBookCard";
import { useQuery } from "hooks/useQuery";
import { ScientificPublicationsService } from "services/scientificPublicationsService";
import { OperatorFilter } from "services/shared-models/sharedEnums";
import { ScientificPublishingAttachment } from "views/home/scientificPublication/form/ScientificPublicationForm.types";
import useMutation from "hooks/useMutation";
import { FileService } from "services/fileService";
import { useState } from "react";
import Modal from "components/blocks/Modal";
import { useTitle } from "hooks/useTitle";

const extensionsMapper = {
  pdf: "pdf",
  doc: "doc",
  docx: "doc",
  epub: "epub",
};

type FilePreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  file: ScientificPublishingAttachment | null;
};

const FilePreviewModal = ({ isOpen, onClose, file }: FilePreviewModalProps) => {
  const { t } = useTranslation("ScientificPublication");
  const baseUrl = window.location.origin;

  if (!file || !file.file) return null;

  // Use PreviewFile endpoint for preview (inline display)
  const fileUrl = `${baseUrl}/api/FileManager/PreviewFile/${file.file.id}`;
  // Remove dot if extension starts with it
  const fileExtension = file.file.extension?.toLowerCase().replace(/^\./, "");

  const isPDF = fileExtension === "pdf";
  const isImage = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "tiff", "tif"].includes(fileExtension || "");

  const getFileIcon = (extension?: string) => {
    if (!extension) return "icon-document";
    const ext = extension.toLowerCase();
    const mappedIcon = extensionsMapper[ext as keyof typeof extensionsMapper];
    return mappedIcon ? `icon-${mappedIcon}-big` : "icon-document";
  };

  const formatFileSize = (bytes?: string) => {
    if (!bytes) return "N/A";
    const size = parseInt(bytes);
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="mb-0">{file.file.name}</h5>
        </div>

        <div className="modal-body" style={{ maxHeight: "70vh", overflow: "auto" }}>
          {isPDF ? (
            // PDF Viewer
            <div style={{ width: "100%", height: "600px" }}>
              <iframe
                src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                style={{ width: "100%", height: "100%", border: "none" }}
                title={file.translatedName}
              />
            </div>
          ) : isImage ? (
            // Image Viewer
            <div className="text-center">
              <img
                src={fileUrl}
                alt={file.translatedName}
                style={{ maxWidth: "100%", maxHeight: "600px", objectFit: "contain" }}
              />
            </div>
          ) : (
            // File Metadata Display
            <div className="d-flex flex-column align-items-center gap-4 py-5">
              <span className={`${getFileIcon(file.file.extension)} fs-1`} style={{ fontSize: "120px" }} />

              <div className="w-100">
                <div className="row mb-3">
                  <div className="col-4 fw-bold">{t("Common:file")}:</div>
                  <div className="col-8">{file.file.name}</div>
                </div>
                <div className="row mb-3">
                  <div className="col-4 fw-bold">{t("translatorName")}:</div>
                  <div className="col-8">{file.translatedName}</div>
                </div>
                <div className="row mb-3">
                  <div className="col-4 fw-bold">{t("Common:description")}:</div>
                  <div className="col-8">{file.description}</div>
                </div>
                <div className="row mb-3">
                  <div className="col-4 fw-bold">{t("language")}:</div>
                  <div className="col-8">{file.fileLang?.fileLangDetail.displayName}</div>
                </div>
                <div className="row mb-3">
                  <div className="col-4 fw-bold">{t("Common:fileSize")}:</div>
                  <div className="col-8">{formatFileSize(file.file.size)}</div>
                </div>
                <div className="row mb-3">
                  <div className="col-4 fw-bold">{t("Common:fileType")}:</div>
                  <div className="col-8">{file.file.extension?.toUpperCase()}</div>
                </div>
              </div>

              <div className="alert alert-info w-100">
                <span className="icon-info-circle me-2" />
                {t("Common:downloadFileToView")}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

type FileCardProps = {
  file: ScientificPublishingAttachment;
  onDownload: (fileId: string, fileName: string) => void;
  onPreview: (file: ScientificPublishingAttachment) => void;
  isDownloading: boolean;
};

export const FileCard = ({ file, onDownload, onPreview, isDownloading }: FileCardProps) => {
  const { t } = useTranslation("ScientificPublication");
  const isDownloadAllowed = file.isAllowedFileToDownload;

  const getFileIcon = (extension?: string) => {
    if (!extension) return "icon-document";
    const ext = extension.toLowerCase();
    const mappedIcon = extensionsMapper[ext as keyof typeof extensionsMapper];
    return mappedIcon ? `icon-${mappedIcon}-big` : "icon-document";
  };

  const handlePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPreview(file);
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isDownloading && file.file?.id && file.file?.name) {
      onDownload(file.file.id, file.file.name);
    }
  };

  return (
    <div className="file-card flex-1 d-flex flex-column gap-3 p-4 bg-white rounded-4 shadow">
      <div className="d-flex gap-2 align-items-start">
        <span className={`${getFileIcon(file.file?.extension)} fs-48px`} />
        <div className="flex-1">
          <h6 className="mb-1 fw-bold">{file.fileLang?.fileLangDetail.displayName}</h6>
          <p className="m-0 text-gray">{file.translatedName}</p>
          <p className="mt-3 mb-0">{file.description}</p>
        </div>
      </div>

      <div className="d-flex gap-2 flex-wrap">
        {/* Preview Button - Always Available */}
        <button
          className="btn btn-outline-primary btn-sm flex-1"
          onClick={handlePreview}
          type="button"
        >
          <span className="icon-eye me-2" />
          {t("Common:show")}
        </button>

        {/* Download Button - Only if allowed */}
        {isDownloadAllowed ? (
          <button
            className="btn btn-primary btn-sm flex-1"
            onClick={handleDownload}
            disabled={isDownloading}
            type="button"
          >
            {isDownloading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                {t("downloading")}
              </>
            ) : (
              <>
                <span className="icon-download me-2" />
                {t("Common:download")}
              </>
            )}
          </button>
        ) : (
          <div className="d-flex align-items-center gap-2 flex-1 justify-content-center text-danger">
            <span className="icon-lock" />
            <span className="small">{t("notAllowed")}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const PublicationsDetails = () => {
  const { isAr } = useCookieContext();
  const { toSystemThemeDateFormat } = useSystemDateTheme();
  const { t, i18n } = useTranslation("ScientificPublication");
  useTitle(t("scientificPublications"));

  const { id } = useParams();
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    file: ScientificPublishingAttachment | null;
  }>({
    isOpen: false,
    file: null,
  });

  // File download mutation
  const { mutateAsync: downloadFile, loading: fileDownloadLoading } = useMutation({
    queryFn: async ({ id, fileName }: { id: string; fileName: string }) => {
      await FileService.downloadFile(id, fileName);
      return { hasError: false, data: null, description: "", code: 200 };
    },
  });

  const handleFileDownload = (fileId: string, fileName: string) => {
    if (fileDownloadLoading) return;
    downloadFile({ id: fileId, fileName });
  };

  const handleFilePreview = (file: ScientificPublishingAttachment) => {
    setPreviewModal({
      isOpen: true,
      file: file,
    });
  };

  const handleClosePreview = () => {
    setPreviewModal({
      isOpen: false,
      file: null,
    });
  };

  const { data: scientificPublication, loading: scientificPublicationLoading } = useQuery({
    queryFn: async () => {
      return await ScientificPublicationsService.getPublicScientificPublishingById(id ? +id : 0);
    },
    options: { enabled: !!id },
    triggers: [isAr, id],
  });

  const { data: allPublicScientificPublishing, loading: allPublicScientificPublishingLoading } = useQuery({
    queryFn: async () => {
      return await ScientificPublicationsService.getAllPublicScientificPublishing({
        pageIndex: 1,
        pageSize: 10,
        filter: [
          {
            field: "TypeOfScientificId",
            value: scientificPublication?.typeOfScientific?.id,
            operator: OperatorFilter.equal,
          },
        ],
      });
    },
    options: { enabled: !!scientificPublication },
    triggers: [scientificPublication],
  });

  return (
    <div className=" bg-white d-flex flex-column gap-4 position-relative text-dark">
      <LoadingOverlay visible={scientificPublicationLoading} className="h-100" />

      <div className="shadow py-1  px-6rem ">
        <NavBar tapClassName="text-dark" variant="dark" />
      </div>

      <div className="publications-details-container d-flex flex-column flex-md-row gap-4 pb-5">
        {/* Right Side (Image, Info, etc.) */}
        <div className="details-left-side d-flex flex-column gap-4 align-items-center">
          <img
            className="rounded-1 book-image"
            src={scientificPublication?.scientificPublishingImageUrl}
            alt="publication"
          />
          <div className="d-flex w-100 justify-content-between align-items-center gap-2">
            <div className="text-center">
              <p className="m-0">{t("numberOfPages")}</p>
              <p className="m-0 fw-bold">{scientificPublication?.numberOfPaper}</p>
            </div>
            <div className="h-50 border-start" />
            <div className="text-center">
              <p className="m-0">{t("scientificField")}</p>
              <p className="m-0 fw-bold">
                {scientificPublication?.typeOfScientific?.typeOfScientificDetail.displayName}
              </p>
            </div>
          </div>

          {/* TODO: ask about it */}
          <img className="barcode mt-2" src={require("./temp-barcode.svg").default} alt="barcode" />
        </div>
        {/* End of Right Side */}

        {/* Left Side (Info, etc.) */}
        <div className="border-start d-none d-md-block" />
        <div className="flex-1 d-flex flex-column gap-2" style={{ minWidth: 0 }}>
          <div className="d-flex gap-2">
            <p className="m-0 fs-12px">{t("scientificPublications")}</p>
            <span className="icon-arrow-left fs-20px" />
            <p className="m-0 fs-12px">{scientificPublication?.scientificPublishingDetail?.displayName}</p>
          </div>

          {/* Header */}
          <div>
            <h5 className="mt-4 fw-bold"> {scientificPublication?.scientificPublishingDetail?.displayName}</h5>
            <p className="mt-3">{scientificPublication?.scientificPublishingDetail?.description}</p>
          </div>
          {/* End of Header */}

          {/* Striped Table Info */}
          <div className="striped d-flex flex-column position-relative ">
            <div className="d-flex flex-1 p-3">
              <span className="flex-1 fw-bold text-dark">{t("scientificPublicationNumber")}</span>
              <span className="flex-1">{scientificPublication?.serialNumber}</span>
            </div>
            <div className="d-flex flex-1 p-3">
              <span className="flex-1 fw-bold text-dark">{t("publisherName")}</span>
              <span className="flex-1">{scientificPublication?.authorName} </span>
            </div>
            <div className="d-flex flex-1 p-3">
              <span className="flex-1 fw-bold text-dark">{t("publicationTypeCategory")}</span>
              <span className="flex-1">
                {scientificPublication?.scientificClassification?.scientificClassificationDetail.displayName}
              </span>
            </div>
            <div className="d-flex flex-1 p-3">
              <span className="flex-1 fw-bold text-dark">{t("publicationLanguage")}</span>
              <span className="flex-1">
                {scientificPublication?.languageOfScientific?.languageOfScientificDetail.displayName}
              </span>
            </div>
            <div className="d-flex flex-1 p-3">
              <span className="flex-1 fw-bold text-dark">{t("publicationDate")}</span>
              <span className="flex-1">{toSystemThemeDateFormat(scientificPublication?.publicationDate)}</span>
            </div>
            <div className="d-flex flex-1 p-3">
              <span className="flex-1 fw-bold text-dark">{t("publicationOwner")}</span>
              <span className="flex-1">{scientificPublication?.publisher?.publisherDetail.displayName} </span>
            </div>
            <div className="d-flex flex-1 p-3">
              <span className="flex-1 fw-bold text-dark">{t("publicationOwnerType")}</span>
              <span className="flex-1">{scientificPublication?.publisherType?.publisherTypeDetail.displayName} </span>
            </div>
          </div>
          {/* End of Striped Table Info */}

          <div className="divider my-4" />

          {/* Files */}
          <div>
            <h5 className="mb-4">{t("scientificPublications")}</h5>
            <div className="d-flex flex-wrap gap-3">
              {scientificPublication
                ? scientificPublication?.scientificPublishingAttachments.map((ele, index) => {
                    return (
                      <FileCard
                        key={index}
                        file={ele}
                        onDownload={handleFileDownload}
                        onPreview={handleFilePreview}
                        isDownloading={fileDownloadLoading}
                      />
                    );
                  })
                : t("Common:noData")}
            </div>
          </div>
          {/* End of Files */}

          <div className="divider mb-2 mt-4" />

          {/* Libraries */}
          <div>
            <h5 className="mb-4">{t("availableIn")}</h5>
            <div className="d-flex flex-wrap gap-3">
              {scientificPublication?.libraries.map((ele, index) => (
                <div key={index} className="p-4 bg-white rounded-4 align-items-center shadow">
                  <h6 className="mb-1 fw-bold text-center">{ele.libraryDetail.displayName} </h6>
                  <p className="m-0 text-gray text-center">الموقع</p>
                  {/* TODO: publ location */}
                </div>
              ))}
            </div>
          </div>
          {/* End of Libraries */}

          <div className="divider mb-3 mt-4" />

          {/* Similar Publications */}
          <div className="d-flex flex-column gap-3">
            <h5 className="mb-4">{t("similarPublications")}</h5>
            <div className="d-flex gap-2 flex-wrap">
              {allPublicScientificPublishingLoading ? (
                <div className="custom-loader"></div>
              ) : (
                allPublicScientificPublishing?.map((ele, index) => (
                  <PublicationBookCard key={index} scientificPublishing={ele} />
                ))
              )}
            </div>
          </div>
          {/* End of Similar Publications */}

          {/* End of Left Side */}
        </div>
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={previewModal.isOpen}
        onClose={handleClosePreview}
        file={previewModal.file}
      />
    </div>
  );
};

export default PublicationsDetails;
